import React, { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { Gender, Specialization } from '@docbay/schemas';

import {
  fetchPerClinicsMetrics,
  fetchSpecializations,
} from 'application/store/reducers/Statistics/ActionCreators';
import { genderList, metricsList } from 'features/feature-statistics/constants';
import {
  setMetricsData,
  setPerClinicsFilters,
} from 'application/store/reducers/Statistics/StatisticsSlice';
import { getLanguageFromLocalStorage } from 'application/sessionStorage/languages';

import Dropdown from 'common/components/Dropdown';
import { useAppDispatch, useAppSelector } from 'common/hooks/redux';
import { CountryDropdown, Input, Loader } from 'common/components';
import DatePicker from 'common/components/DatePicker';
import Pagination from 'common/components/Pagination';
import { useDebounce } from 'common/hooks/useDebounce';
import { SupportedCountriesDto } from 'common/types/countries';

import PerClinicsMetricsTable from 'features/feature-statistics/components/PerClinicsMetricsTable';
import {
  onCityChange,
  onDateChange,
  removeKeysFromObject,
} from '../../helpers';

import { PerClinicsDataDto, PerClinicsProps } from './models';
import {
  AgeWrapper,
  DateWrapper,
  FilterSection,
  LeftFilters,
  MetricsFilter,
  Wrapper,
} from './styles';
import { useStatisticFilters } from 'common/hooks/useStatisticFilters';

const PerClinics: FC<PerClinicsProps> = ({
  supportedCountries,
  setSupportedCountries,
}) => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const [currentPage, setCurrentPage] = useState(1);
  const [currentCities, setCurrentCities] = useState<string[]>([]);
  const [currentMetrics, setMetrics] = useState<string[]>([]);
  const [timeFrom, setTimeFrom] = useState<Date | null>(null);
  const [timeTo, setTimeTo] = useState<Date | null>(null);
  const [ageFrom, setAgeFrom] = useState('');
  const [ageTo, setAgeTo] = useState('');
  const [isAgeError, setIsAgeError] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [citiesNames, setCitiesNames] = useState<string[]>([]);
  const [gender, setGender] = useState<string[]>([]);
  const [specializationIds, setSpecializationIds] = useState<string[]>([]);
  const { cities, fetchCities } = useStatisticFilters();

  const { isLoading, metrics, perClinicsMetricsData, specializations } =
    useAppSelector((state) => state.statistics);

  const debouncedInput = useDebounce(inputValue, 500);
  const debouncedAgeFrom = useDebounce(ageFrom, 500);
  const debouncedAgeTo = useDebounce(ageTo, 500);

  const getCurrenData = async (data?: PerClinicsDataDto) => {
    if (isAgeError) return;

    const page = data?.page || currentPage;
    const cities = data?.citiesNames || citiesNames;
    const dataTimeFrom = data?.timeFrom || timeFrom;
    const dataTimeTo = data?.timeTo || timeTo;
    const dataAgeFrom = data?.ageFrom || ageFrom;
    const dataAgeTo = data?.ageTo || ageTo;
    const dataGender = data?.gender || gender;
    const dataSpecializationIds = data?.specializationIds || specializationIds;
    const dataSearchText =
      data?.searchText === '' ? data.searchText : inputValue;

    const params = {
      page: page,
      limit: 10,
      ...(dataAgeFrom?.length || dataAgeTo.length
        ? {
            ageRange: {
              min: dataAgeFrom && dataAgeFrom !== '0' ? +dataAgeFrom : 1,
              max:
                dataAgeTo && dataAgeTo !== '0' && Number(dataAgeTo) < 121
                  ? +dataAgeTo
                  : 120,
            },
          }
        : {}),
      ...(dataGender.length ? { gender: dataGender as Gender[] } : {}),
      ...(dataSearchText ? { search: dataSearchText } : {}),
      ...(cities.length ? { cities: cities } : {}),
      ...(dataSpecializationIds.length
        ? { specializationIds: dataSpecializationIds }
        : {}),
      ...(dataTimeFrom
        ? {
            timeRange: {
              start: dayjs(dataTimeFrom).utc(true).toDate() as Date,
              end: (dayjs(dataTimeTo).utc(true).format() ||
                dayjs().utc(true).toDate()) as Date,
            },
          }
        : {}),
      ...(supportedCountries.length
        ? {
            supportedCountries: supportedCountries.map((item) => item.code),
          }
        : {}),
    };
    await dispatch(fetchPerClinicsMetrics(params));

    // remove page and limit from the object since we do not want to limit the statistics to pagination parameters
    dispatch(
      setPerClinicsFilters(removeKeysFromObject(params, ['page', 'limit'])),
    );
  };

  useEffect(() => {
    dispatch(fetchSpecializations());
  }, []);

  useEffect(() => {
    fetchCities(supportedCountries);
    getCurrenData({ page: 1 });
  }, [supportedCountries]);

  useEffect(() => {
    setMetrics(metrics);
  }, [metrics]);

  const citiesOptions = useMemo(() => {
    const formatted = cities?.map((item) => {
      return { label: item.city, value: item.id };
    });

    return formatted;
  }, [cities]);

  const specializationsOptions = useMemo(() => {
    const currentLanguage = getLanguageFromLocalStorage();
    const specializationLangKey = `name_${currentLanguage.toUpperCase()}` as
      | 'name_EN'
      | 'name_PT';

    const options = specializations.map((item) => {
      const specializationName =
        item[specializationLangKey as keyof Specialization];

      return {
        value: item.id,
        label: specializationName as string,
      };
    });
    return options;
  }, [specializations, i18n.language]);

  useEffect(() => {
    if (debouncedAgeFrom.length > 0 || debouncedAgeTo.length > 0) {
      getCurrenData({ page: 1 });
    }
  }, [debouncedAgeFrom, debouncedAgeTo]);

  const handleCityChange = (data: string[]) => {
    const citiesData = onCityChange(data, citiesOptions);

    getCurrenData({ page: 1, citiesNames: citiesData.citiesLabels });
    setCurrentCities(citiesData.data);
    setCitiesNames(citiesData.citiesLabels);
  };

  const clearSearch = () => {
    getCurrenData({ page: 1, searchText: '' });
    setInputValue('');
  };

  const handleSearch = (value: string) => {
    setInputValue(value);
    if (!value) {
      clearSearch();
    }
  };

  useEffect(() => {
    if (debouncedInput.length > 2) {
      getCurrenData({ page: 1 });
    }
  }, [debouncedInput]);

  const handleGenderChange = (data: string[]) => {
    getCurrenData({ gender: data, page: 1 });
    setGender(data);
  };

  const handleDateToChange = (date: string) => {
    const dateTimeTo = onDateChange(date, 'to');

    getCurrenData({ page: 1, timeTo: dateTimeTo });
    setTimeTo(timeTo!);
  };

  const handleDateFromChange = (date: string) => {
    const dateTimeFrom = onDateChange(date, 'from');

    getCurrenData({
      page: 1,
      timeFrom: dateTimeFrom,
      ...(!timeTo
        ? { timeTo: dayjs().set('hours', 23).set('minutes', 59).toDate() }
        : { timeTo }),
    });

    if (!timeTo) {
      setTimeTo(dayjs().set('hours', 23).set('minutes', 59).toDate());
    }
    setTimeFrom(dateTimeFrom);
  };

  const handleAgeChange = (value: string, type: string) => {
    if (type === 'from') {
      setIsAgeError(Number(value) > Number(ageTo ? ageTo : 120));
      setAgeFrom(value);
    } else {
      setIsAgeError(Number(value) < Number(ageFrom ? ageFrom : 1));
      setAgeTo(value);
    }
  };

  const handlePagination = (page: number) => {
    getCurrenData({ page });
    setCurrentPage(page);
  };

  return (
    <Wrapper>
      {isLoading && <Loader />}
      <FilterSection>
        <LeftFilters>
          <div>
            <Input
              id={'patientName'}
              type="search"
              value={inputValue}
              onClear={clearSearch}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder={t('statistics.search_by_clinics_name')}
            />
            <DateWrapper>
              <DatePicker
                id={'from'}
                label={t('statistics.period_from')}
                value={dayjs(timeFrom).format()}
                onChange={(date) => {
                  handleDateFromChange(date);
                }}
              />
              <DatePicker
                id={'to'}
                label={t('statistics.period_to')}
                value={dayjs(timeTo).format()}
                maxDate={Date.now()}
                onChange={(date) => {
                  handleDateToChange(date);
                }}
              />
            </DateWrapper>
            <CountryDropdown
              id="country-dropdown"
              value={supportedCountries}
              onChange={(value) => {
                setSupportedCountries(value as SupportedCountriesDto[]);
              }}
              isMulti={true}
            />
            <Dropdown
              id="cities"
              placeholder={t('statistics.cities')}
              isMulti
              withSearch
              value={currentCities}
              onChange={(data) => {
                handleCityChange(data as string[]);
              }}
              options={citiesOptions!}
            />
          </div>
          <div>
            <AgeWrapper>
              <Input
                id={'ageFrom'}
                type="number"
                value={ageFrom}
                onChange={(e) => handleAgeChange(e.target.value, 'from')}
                placeholder={t('statistics.age_from')}
              />
              <Input
                id={'ageTo'}
                type="number"
                value={ageTo}
                onChange={(e) => handleAgeChange(e.target.value, 'to')}
                placeholder={t('statistics.age_to')}
              />
            </AgeWrapper>
            <Dropdown
              id="gender"
              placeholder={t('statistics.gender')}
              isMulti
              value={gender}
              onChange={(data) => {
                handleGenderChange(data as string[]);
              }}
              options={genderList!}
            />
            <Dropdown
              id={'specializations'}
              value={specializationIds}
              placeholder={t('statistics.specializations')}
              onChange={(value) => {
                getCurrenData({
                  page: 1,
                  specializationIds: value as string[],
                });
                setSpecializationIds(value as string[]);
              }}
              options={specializationsOptions}
              withSearch={true}
              isMulti={true}
            />
          </div>
        </LeftFilters>
        <MetricsFilter>
          <Dropdown
            id="metrics"
            placeholder={t('statistics.metrics')}
            isMulti
            withSearch={false}
            value={currentMetrics}
            onChange={(data) => {
              dispatch(setMetricsData(data));
            }}
            options={metricsList!}
          />
        </MetricsFilter>
      </FilterSection>
      <PerClinicsMetricsTable />
      {perClinicsMetricsData?.perClinic?.entities?.length ? (
        <Pagination
          pages={perClinicsMetricsData?.perClinic?.totalPages || 1}
          setCurrentPage={handlePagination}
          currentPage={currentPage}
          itemCount={perClinicsMetricsData?.perClinic?.entities?.length || 0}
        />
      ) : (
        ''
      )}
    </Wrapper>
  );
};

export default PerClinics;
